import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Typography } from "@mui/material";
import { Snackbar, Alert, AlertTitle } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  emailValidation,
  getEmail,
  getUserById,
  updateSubUserImageById,
  updateUserById,
  updateUserImageById,
} from "../../Api/Services/UserServices";
import SuccessIcon from "../../Assets/Images/success.png";
import { PAWPRINT_USER_ROLE_KEY } from "../../Constant";
import {
  Body,
  ButtonBoxStyle,
  Column,
  FormHeaderBox,
  newSmallFieldBoxStyle,
} from "../../Css";
import { UserProfileEditValidation } from "../../From/Validation";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import InputField from "../Common/InputField";
import { showSnackbar } from "../Common/Snackbar";
import "./settingScroll.css";
import { uploadS3Bucket } from "../../Api/axios";
import { useTheme } from "@mui/material/styles";

// Custom hook for user role checking
const useUserRole = () => {
  return useMemo(() => {
    const role = localStorage.getItem(PAWPRINT_USER_ROLE_KEY);
    return {
      isUser: role === "User",
      isGeneralAuthorizedUser: role === "General Authorized User",
      isViewOnlyUser: role === "View Only User",
      hasEditPermission: role === "User" || role === "General Authorized User" || role === "View Only User"
    };
  }, []);
};

// Custom hook for profile data management
const useProfileData = (userId) => {
  const [profileDetails, setProfileDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialValue, setInitialValue] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [email2, setEmail2] = useState("");
  const { hasEditPermission } = useUserRole();

  useEffect(() => {
    const fetchProfileData = async () => {
      if (hasEditPermission && userId) {
        try {
          const response = await getUserById(userId);
          const userData = response.data.data;
          setProfileDetails(userData);
          setInitialValue({
            first_name: userData.first_name,
            email: userData.email,
            last_name: userData.last_name,
          });
          setEmail2(userData.email);
          setIsLoading(false);
        } catch (error) {
          showSnackbar({
            type: "error",
            message: "Failed to fetch profile data",
          });
          setIsLoading(false);
        }
      }
    };

    fetchProfileData();
  }, [userId, hasEditPermission]);

  return {
    profileDetails,
    isLoading,
    initialValue,
    email2,
    setProfileDetails,
    setInitialValue,
    setEmail2
  };
};

// Custom hook for email verification
const useEmailVerification = () => {
  const [verifyCode, setVerifyCode] = useState("");
  const [showCodeBox, setShowCodeBox] = useState(false);
  const [verifyButton, setVerifyButton] = useState("0");
  const [secret, setSecret] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const handleVerifyEmail = useCallback(async (email) => {
    try {
      const emailExistsResponse = await getEmail({ email });
      
      if (emailExistsResponse.data.message === "Email Exists.") {
        return { success: false, message: "Email Exists" };
      }

      const validationResponse = await emailValidation({ email });
      
      if (validationResponse.data.status !== "success") {
        return { success: false, message: validationResponse.data.message };
      }

      setSecret(validationResponse.data.secret);
      setVerifyButton("1");
      setShowCodeBox(true);
      setVerifyCode(validationResponse.data.secret);
      
      showSnackbar({
        type: "success",
        message: "Verification Email send. Check your email.",
      });

      return { success: true };
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Verification failed",
      });
    }
  }, []);

  const handleVerifyCode = useCallback((inputCode) => {
    if (inputCode === secret) {
      setVerifyButton("2");
      setShowCodeBox(false);
      return { success: true };
    } else {
      return { 
        success: false, 
        message: "Verification Code is incorrect. Please enter Valid Code" 
      };
    }
  }, [secret]);

  return {
    verifyCode,
    showCodeBox,
    verifyButton,
    isButtonDisabled,
    handleVerifyEmail,
    handleVerifyCode,
    setVerifyCode,
    setShowCodeBox,
    setVerifyButton,
    setIsButtonDisabled
  };
};

// Custom hook for image upload
const useImageUpload = (userId) => {
  const [image, setImage] = useState("");
  const { isUser, isGeneralAuthorizedUser, isViewOnlyUser } = useUserRole();

  const updateProfileImage = useCallback(async (values) => {
    if (!image) return;

    const formData = new FormData();
    if (image instanceof File) {
      formData.append("image", image);
    }

    try {
      if (isUser) {
        const updateData = {
          type: image?.type,
          file_name: `${Date.now()}-${image?.name}`,
        };
        const res = await updateUserImageById(userId, updateData);
        const url = res?.data?.url;
        const decodedUrl = decodeURIComponent(url);
        const uploadRes = await uploadS3Bucket(decodedUrl, image);
        
        if (uploadRes?.status === 200) {
          showSnackbar({
            type: "success",
            message: "Profile information updated successfully",
          });
          return { success: true };
        }
      } else if (isGeneralAuthorizedUser || isViewOnlyUser) {
        await updateSubUserImageById(userId, formData);
        showSnackbar({
          type: "success",
          message: "Profile information updated successfully",
        });
        return { success: true };
      }
    } catch (error) {
      console.error("error: ", error);
      return { 
        success: false, 
        message: error.response?.data?.message || "Image upload failed" 
      };
    }
  }, [image, userId, isUser, isGeneralAuthorizedUser, isViewOnlyUser]);

  return {
    image,
    setImage,
    updateProfileImage
  };
};

export default function UserProfileEdit() {
  const user_id = localStorage.getItem("pawprint_user_id");
  const navigate = useNavigate();
  const theme = useTheme();
  const formRef = useRef(null);
  const inputRef = useRef(null);

  // Custom hooks
  const { hasEditPermission } = useUserRole();
  const { profileDetails, isLoading, initialValue, email2 } = useProfileData(user_id);
  const {
    verifyCode,
    showCodeBox,
    verifyButton,
    isButtonDisabled,
    handleVerifyEmail,
    handleVerifyCode,
    setVerifyCode,
    setShowCodeBox,
    setVerifyButton,
    setIsButtonDisabled
  } = useEmailVerification();
  const { image, setImage, updateProfileImage } = useImageUpload(user_id);

  // State management
  const [open, setOpen] = useState(false);
  const [openSuccessMessage, setOpenSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(false);

  // Memoized values
  const verifybtnStyle = useMemo(() => ({
    marginLeft: "1vw",
    mx: 1,
    color: "white !important",
    background: `${theme.palette.primary.main} !important`,
    border: "2px solid rgba(255, 255, 255, 0.5)",
    boxshadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
    backdropFilter: "blur(10px)",
    borderRadius: "11px",
    width: 80,
    textTransform: "none",
    fontSize: "13px",
    "&:hover": {
      border: "2px solid white",
    },
  }), [theme.palette.primary.main]);

  // Event handlers
  const onChange = useCallback((e, name) => {
    formRef.current?.setFieldValue(name, e);
    if (e === email2) {
      setDisableSubmit(false);
      setIsButtonDisabled(true);
    } else {
      setDisableSubmit(true);
      setIsButtonDisabled(false);
    }
  }, [email2]);

  const handleSubmit = useCallback(async (values, { setSubmitting }) => {
    setDisableSubmit(true);
    const userData = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
    };

    if (!hasEditPermission) {
      setErrorMessage("You do not have permission to update the profile.");
      setOpen(true);
      setDisableSubmit(false);
      setSubmitting(false);
      return;
    }

    try {
      await updateUserById(user_id, userData);
      
      if (image) {
        const imageResult = await updateProfileImage(values);
        if (!imageResult.success) {
          setErrorMessage(imageResult.message);
          setOpen(true);
          setDisableSubmit(false);
          setSubmitting(false);
          return;
        }
      }

      showSnackbar({
        type: "success",
        message: "Profile information updated successfully",
      });
      setDisableSubmit(false);
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
      setOpen(true);
      setErrorMessage(error.response?.data?.message || "Update failed");
      setDisableSubmit(false);
    }
  }, [user_id, image, hasEditPermission, updateProfileImage]);

  const handleVerifyEmailClick = useCallback(async () => {
    const email = formRef?.current?.values?.email;
    if (!email) return;

    const result = await handleVerifyEmail(email);
    if (!result.success) {
      setErrorMessage(result.message);
      setOpen(true);
    }
  }, [handleVerifyEmail]);

  const handleVerifyCodeClick = useCallback(async () => {
    const inputCode = formRef?.current?.values?.verifyCode;
    if (!inputCode) return;

    const result = handleVerifyCode(inputCode);
    if (!result.success) {
      setErrorMessage(result.message);
      setOpen(true);
    } else {
      setDisableSubmit(false);
    }
  }, [handleVerifyCode]);

  const handleImageChange = useCallback((e) => {
    setImage(e.target.files[0]);
  }, []);

  const handleImageRemove = useCallback(() => {
    setImage(null);
  }, []);

  const handleImageClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSnackbarClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSuccessSnackbarClose = useCallback(() => {
    setOpenSuccessMessage(false);
  }, []);

  // Render verification button
  const renderVerificationButton = useCallback(() => {
    if (verifyButton === "0") {
      return (
        <Box sx={{ marginTop: "15px" }}>
          <Button
            sx={{ ...verifybtnStyle, width: "100%" }}
            variant="contained"
            disabled={isButtonDisabled}
            onClick={handleVerifyEmailClick}
            style={{ color: "white" }}
          >
            Verify
          </Button>
        </Box>
      );
    } else if (verifyButton === "1") {
      return (
        <Box sx={{ marginTop: "15px" }}>
          <Button
            sx={{ ...verifybtnStyle, width: "100%" }}
            variant="contained"
            disabled={isButtonDisabled}
            onClick={handleVerifyCodeClick}
          >
            Submit
          </Button>
        </Box>
      );
    } else {
      return (
        <Box>
          <img
            src={SuccessIcon}
            alt="SuccessIcon"
            style={{ height: 40, width: 40 }}
          />
        </Box>
      );
    }
  }, [verifyButton, isButtonDisabled, verifybtnStyle, handleVerifyEmailClick, handleVerifyCodeClick]);

  // Render profile image
  const renderProfileImage = useCallback(() => {
    if (image && image instanceof File) {
      return (
        <div style={{ position: "relative" }}>
          <img
            src={URL.createObjectURL(image)}
            style={{
              objectFit: "cover",
              width: "200px",
              height: "200px",
              cursor: "pointer",
              borderRadius: "50%",
              border: "2px solid rgba(255, 255, 255, 0.5)",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
              backgroundColor: `${theme.palette.primary.main}`,
            }}
            alt="user_image"
            onClick={handleImageClick}
          />
          <CloseIcon
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 1,
              cursor: "pointer",
              backgroundColor: "white",
              borderRadius: "50%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
            }}
            onClick={handleImageRemove}
          />
        </div>
      );
    } else if (profileDetails?.image) {
      return (
        <img
          src={profileDetails?.image}
          style={{
            objectFit: "cover",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            border: "2px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
          }}
          alt="profile"
          onClick={handleImageClick}
          onMouseEnter={(e) => {
            e.target.style.opacity = 0.8;
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = 1;
          }}
        />
      );
    } else {
      return (
        <AccountCircleIcon
          sx={{
            width: {md:'150px',sm:'120px',xs:'100px'},
            height: {md:'150px',sm:'120px',xs:'100px'},
            color: "black",
          }}
          onClick={handleImageClick}
          onMouseEnter={(e) => {
            e.target.style.opacity = 0.8;
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = 1;
          }}
        />
      );
    }
  }, [image, profileDetails?.image, theme.palette.primary.main, handleImageClick, handleImageRemove]);

  return (
    <Container>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="error" onClose={handleSnackbarClose}>
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openSuccessMessage}
        autoHideDuration={6000}
        onClose={handleSuccessSnackbarClose}
      >
        <Alert severity="success" onClose={handleSnackbarClose}>
          <AlertTitle>Success</AlertTitle>
          {successMessage}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "left",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <ArrowBackIosNewRoundedIcon
          sx={{ color: "black", cursor: "pointer" }}
          onClick={handleBackClick}
        />
        <FormTitle>Edit Your Profile Details</FormTitle>
      </Box>
      {isLoading ? (
        <Typography
          variant="subtitle1"
          style={{ color: "#000000", textAlign: "center", marginTop: "5vw" }}
        >
          Loading your data...
        </Typography>
      ) : (
        <FormWrapper>
          <Formik
            initialValues={initialValue}
            innerRef={formRef}
            onSubmit={handleSubmit}
            validationSchema={UserProfileEditValidation}
            validateOnMount={false}
            enableReinitialize
          >
            {({ getFieldProps, values, setFieldValue, isSubmitting }) => (
              <Form>
                <Box sx={FormHeaderBox(theme.palette.primary.main)}>
                  <div
                    className="scroll2"
                    style={{ maxHeight: "calc(100vh - 239px)", width: "100%" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <input
                        id="userImage"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                        ref={inputRef}
                      />
                      {renderProfileImage()}
                    </Box>
                    <Body style={{ gap: "10px 20px" }}>
                      <Column>
                        <Box
                          sx={{
                            ...newSmallFieldBoxStyle,
                            paddingBottom: "0px",
                          }}
                        >
                          <Box sx={{ width: {sm:"80%",xs:'72%'}  }}>
                            <FormGroup
                              name={showCodeBox ? "verifyCode" : "email"}
                              label={
                                showCodeBox
                                  ? "Verification Code"
                                  : " Email Address"
                              }
                              requiredField
                            >
                              <InputField
                                {...getFieldProps(
                                  showCodeBox ? "verifyCode" : "email"
                                )}
                                onChange={(e) =>
                                  onChange(
                                    e.target.value,
                                    showCodeBox ? "verifyCode" : "email"
                                  )
                                }
                              />
                            </FormGroup>
                          </Box>
                          <Box style={{ width: "20%" }}>
                            {renderVerificationButton()}
                          </Box>
                        </Box>
                      </Column>
                      <Column>
                        <FormGroup
                          name="first_name"
                          label={"First Name"}
                          requiredField
                        >
                          <InputField {...getFieldProps("first_name")} />
                        </FormGroup>
                      </Column>
                      <Column>
                        <FormGroup
                          name="last_name"
                          label={"Last Name"}
                          requiredField
                        >
                          <InputField {...getFieldProps("last_name")} />
                        </FormGroup>
                      </Column>
                    </Body>
                  </div>
                  <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                    <CustomButton
                      type="submit"
                      disabled={isSubmitting}
                      label={isSubmitting ? "Submitting..." : "Submit"}
                    />
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 20px;
  @media (max-width: 450px) {
    padding: 10px;
  }
`;

const FormTitle = styled.h2`
  text-align: center;
  font-size: 20px;
  font-weight: 500;
  font-style: normal;
  color: #000000;
`;

const FormWrapper = styled.div`
  /* display: flex; */
  justify-content: center;
  align-items: center;
  /* flex-direction: column; */
  // margin: 0 auto;
  // padding: 20px;
  width: 100%;
`;

